import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Music, Heart, Mic2, MapPin, Instagram, Facebook } from "lucide-react";
import { LOGO_URL, api } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/12270356/pexels-photo-12270356.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1800";
const BIO_IMG = "/assets/band/01-lighthouse.jpg";
const PALM_IMG = "https://images.unsplash.com/photo-1455278996551-747adfa0f7cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwYWxtJTIwdHJlZXMlMjBzdW5zZXQlMjB2aW50YWdlfGVufDB8fHx8MTc4MDYxMDQ0MXww&ixlib=rb-4.1.0&q=85";

// Venues the band has played that aren't currently in the upcoming gigs schedule
const EVERGREEN_VENUES = ["Terranea", "HT Grill", "The Whisky a Go Go"];

// Strip sub-location suffixes and common venue words so different shows at the
// same venue collapse into one tile ("The Lighthouse Cafe — South Bay Jam"
// and "The Lighthouse" both become "The Lighthouse").
const canonicalize = (name) =>
  name
    .split(/[—:]/)[0]
    .replace(/\bCafe\b/i, "")
    .replace(/\bTavern\b/i, "")
    .replace(/\s+/g, " ")
    .trim();

const Home = () => {
  const [venues, setVenues] = useState([]);

  useEffect(() => {
    api.get("/gigs")
      .then((r) => {
        const seen = new Set();
        const out = [];
        for (const g of r.data || []) {
          const v = canonicalize(g.venue_name);
          if (!v) continue;
          const k = v.toLowerCase();
          if (seen.has(k)) continue;
          seen.add(k);
          out.push(v);
        }
        for (const v of EVERGREEN_VENUES) {
          const k = v.toLowerCase();
          if (!seen.has(k)) {
            seen.add(k);
            out.push(v);
          }
        }
        setVenues(out);
      })
      .catch(() => setVenues(EVERGREEN_VENUES));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover vintage-img" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 w-full py-32">
          <p className="eyebrow fade-up" data-testid="hero-eyebrow">The Southbay's Premier Cover Band</p>
          <h1 className="font-heading text-[18vw] sm:text-[16vw] md:text-[14vw] lg:text-[200px] leading-[0.85] uppercase mt-6 tracking-tighter fade-up-1 whitespace-nowrap" data-testid="hero-title">
            Retrofit
          </h1>
          <p className="mt-8 max-w-xl text-lg md:text-xl text-neutral-200 leading-relaxed fade-up-2" data-testid="hero-tagline">
            Live music for beaches, ballrooms and backyards. Hire the band that turns every night into the one people talk about tomorrow.
          </p>
          <div className="mt-12 flex flex-wrap gap-4 fade-up-3">
            <Link to="/book" className="btn-retro-solid" data-testid="hero-cta-book">
              Book the Band <ArrowRight size={14} className="ml-3" />
            </Link>
            <Link to="/gigs" className="btn-retro" data-testid="hero-cta-gigs">
              Upcoming Gigs
            </Link>
            <Link to="/request" className="btn-retro" data-testid="hero-cta-request">
              Request a Song
            </Link>
            <Link to="/tip" className="btn-retro" data-testid="hero-cta-tip">
              Tip the Band
            </Link>
          </div>

          <div className="mt-20 flex items-center gap-6 fade-up-4">
            <span className="eyebrow">Follow</span>
            <div className="flex items-center gap-5">
              <a href="https://instagram.com/iloveretrofitmusic" target="_blank" rel="noreferrer" className="text-white hover:opacity-60 transition" data-testid="hero-social-instagram"><Instagram size={20}/></a>
              <a href="https://www.facebook.com/retrofitmusic" target="_blank" rel="noreferrer" className="text-white hover:opacity-60 transition" data-testid="hero-social-facebook"><Facebook size={20}/></a>
            </div>
          </div>
        </div>

        {/* Bottom marquee */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/70 backdrop-blur-sm py-4 overflow-hidden">
          <div className="marquee-track flex gap-12 whitespace-nowrap font-heading text-2xl uppercase text-neutral-300">
            {["a", "b"].map((side) => (
              <React.Fragment key={`marquee-${side}`}>
                <span>★ Hermosa Beach</span>
                <span>★ Manhattan Beach</span>
                <span>★ Redondo Beach</span>
                <span>★ Long Beach</span>
                <span>★ El Segundo</span>
                <span>★ Palos Verdes</span>
                <span>★ Torrance</span>
                <span>★ The Southbay</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* BIO / ABOUT */}
      <section className="py-24 md:py-32 bg-neutral-950">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <img src={BIO_IMG} alt="Band on stage" className="w-full aspect-[4/5] object-cover vintage-img" data-testid="bio-image" />
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-black p-4 border-4 border-black">
              <img src={LOGO_URL} alt="Retrofit logo" className="h-24 w-auto object-contain" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow">About the band</p>
            <h2 className="font-heading text-5xl md:text-6xl uppercase mt-4 leading-[0.95]">
              Southbay born.<br />Coastline raised.<br />Endless setlist.
            </h2>
            <div className="mt-8 space-y-5 text-lg text-neutral-300 leading-relaxed max-w-2xl">
              <p>
                Retrofit is a Southbay cover band built in the bars and beach clubs of the California coast.
                We play the songs you scream along to — three decades of rock, pop, soul and dance
                arranged to fill a dancefloor, ten minutes flat.
              </p>
              <p>
                Weddings. Birthdays. Beach bashes. Corporate. If you need a band that can read a room
                and play it like a Friday night at the Lighthouse, we're it.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" className="btn-retro-solid" data-testid="bio-cta-book">Hire Retrofit</Link>
              <Link to="/media" className="btn-retro" data-testid="bio-cta-media">Watch + Listen</Link>
            </div>
          </div>
        </div>
      </section>

      {/* AS SEEN AT */}
      <section className="py-20 md:py-28 bg-black border-t border-white/10" data-testid="venues-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="text-center mb-12 md:mb-16">
            <p className="eyebrow" data-testid="venues-eyebrow">Stages we've owned</p>
            <h2 className="font-heading text-5xl md:text-7xl uppercase mt-5 leading-[0.95]">
              As Seen At
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {venues.map((v) => (
              <div
                key={v}
                data-testid={`venue-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                className="bg-black px-6 py-10 flex items-center justify-center text-center min-h-[140px] group hover:bg-white transition-colors duration-300"
              >
                <span className="font-heading text-xl md:text-2xl lg:text-3xl uppercase leading-tight tracking-tight text-white group-hover:text-black transition-colors">
                  {v}
                </span>
              </div>
            ))}
          </div>

          <p className="mt-10 text-center font-mono text-xs tracking-[0.25em] uppercase text-neutral-500">
            ...and many more · Bars · Beach Clubs · Weddings · Festivals · Private Events
          </p>
        </div>
      </section>

      {/* CTA TILES */}
      <section className="bg-black border-y border-white/10">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-4 divide-x divide-white/10 border-x border-white/10">
          {[
            { to: "/gigs", icon: Calendar, label: "Upcoming Gigs", sub: "Find us live this month", tid: "tile-gigs" },
            { to: "/book", icon: Mic2, label: "Book the Band", sub: "Weddings, parties, corporate", tid: "tile-book" },
            { to: "/request", icon: Music, label: "Request a Song", sub: "Tell us what to play", tid: "tile-request" },
            { to: "/tip", icon: Heart, label: "Tip the Band", sub: "Venmo, Zelle, PayPal, Cash App", tid: "tile-tip" },
          ].map((t) => (
            <Link
              key={t.to}
              to={t.to}
              data-testid={t.tid}
              className="group px-8 py-14 hover:bg-white hover:text-black transition-colors duration-200"
            >
              <t.icon size={28} className="mb-6" />
              <p className="eyebrow group-hover:text-black/60">{t.sub}</p>
              <p className="font-heading text-3xl uppercase mt-3">{t.label}</p>
              <span className="mt-6 inline-flex items-center text-xs font-mono tracking-[0.25em] uppercase">
                Go <ArrowRight size={14} className="ml-2 group-hover:translate-x-1 transition" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* PALM Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={PALM_IMG} alt="" className="w-full h-full object-cover vintage-img opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 text-center">
          <p className="eyebrow" data-testid="palm-eyebrow">Catch us live</p>
          <h2 className="font-heading text-5xl md:text-7xl uppercase mt-5 leading-[0.95]">
            <MapPin className="inline-block mr-3 -mt-3" size={48}/> Where the<br/>palms meet the pier.
          </h2>
          <p className="mt-6 text-lg text-neutral-300 max-w-2xl mx-auto">
            We play every weekend across the Southbay — and we travel anywhere there's a sound system.
          </p>
          <div className="mt-10">
            <Link to="/gigs" className="btn-retro-solid" data-testid="palm-cta-gigs">See the Schedule</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
